import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["active"];
  static values = { direction: { type: String, default: "horizontal" } };

  connect() {
    if (!this.hasActiveTarget) return;

    this.isVertical = this.directionValue === "vertical";
    this.createIndicator();
    this.scrollToActive();
    this.positionIndicator(false);

    this._handleClick = this.handleClick.bind(this);
    this.element.addEventListener("click", this._handleClick);

    if (this.isVertical) {
      this._handleToggle = this.handleToggle.bind(this);
      this.element.querySelectorAll("details").forEach((details) => {
        details.addEventListener("toggle", this._handleToggle);
      });
    }
  }

  disconnect() {
    if (this._handleClick) {
      this.element.removeEventListener("click", this._handleClick);
    }
    if (this._handleToggle) {
      this.element.querySelectorAll("details").forEach((details) => {
        details.removeEventListener("toggle", this._handleToggle);
      });
    }
  }

  createIndicator() {
    this.element.style.position = "relative";

    this.indicator = document.createElement("div");
    this.indicator.classList.add(
      "absolute", "bg-indigo-50", "rounded-md",
      "ring-1", "ring-indigo-200", "shadow-sm",
      "transition-all", "duration-300", "ease-in-out",
      "pointer-events-none"
    );
    this.indicator.style.zIndex = "0";

    this.element.prepend(this.indicator);

    this.element.querySelectorAll("a").forEach((link) => {
      link.style.position = "relative";
      link.style.zIndex = "1";
    });
  }

  offsetRelativeTo(el) {
    let top = 0;
    let left = 0;
    let current = el;

    while (current && current !== this.element) {
      top += current.offsetTop;
      left += current.offsetLeft;
      current = current.offsetParent;
    }

    return { top, left };
  }

  positionIndicator(animate = true) {
    const active = this.activeTarget;

    if (!animate) {
      this.indicator.style.transition = "none";
    }

    if (this.isVertical) {
      const { top, left } = this.offsetRelativeTo(active);
      this.indicator.style.top = `${top}px`;
      this.indicator.style.left = `${left}px`;
      this.indicator.style.width = `${active.offsetWidth}px`;
      this.indicator.style.height = `${active.offsetHeight}px`;
    } else {
      this.indicator.style.top = "4px";
      this.indicator.style.left = `${active.offsetLeft}px`;
      this.indicator.style.width = `${active.offsetWidth}px`;
      this.indicator.style.height = `${active.offsetHeight}px`;
    }

    if (!animate) {
      this.indicator.offsetHeight; // force reflow
      this.indicator.style.transition = "";
    }
  }

  handleClick(e) {
    const link = e.target.closest("a");
    if (!link || !this.element.contains(link)) return;

    if (this.isVertical) {
      const { top, left } = this.offsetRelativeTo(link);
      this.indicator.style.top = `${top}px`;
      this.indicator.style.left = `${left}px`;
      this.indicator.style.height = `${link.offsetHeight}px`;
      this.indicator.style.width = `${link.offsetWidth}px`;
    } else {
      this.indicator.style.left = `${link.offsetLeft}px`;
      this.indicator.style.width = `${link.offsetWidth}px`;
    }
  }

  handleToggle() {
    if (!this.hasActiveTarget) return;

    this.element.querySelectorAll("a").forEach((link) => {
      link.style.position = "relative";
      link.style.zIndex = "1";
    });

    const active = this.activeTarget;
    const isHidden = active.closest("details") && !active.closest("details").open;

    if (isHidden) {
      this.indicator.style.opacity = "0";
    } else {
      this.indicator.style.opacity = "1";
      this.positionIndicator(true);
    }
  }

  scrollToActive() {
    const container = this.element;
    const active = this.activeTarget;

    if (this.isVertical) {
      const { top } = this.offsetRelativeTo(active);
      container.scrollTop = top - container.offsetHeight / 2 + active.offsetHeight / 2;
    } else {
      container.scrollLeft = active.offsetLeft - container.offsetWidth / 2 + active.offsetWidth / 2;
    }
  }
}
